import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ConfirmationDialog, ConfirmationDialogProps } from './ConfirmationDialog';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonColor?: 'error' | 'warning' | 'primary' | 'secondary';
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

interface ConfirmationProviderProps {
  children: ReactNode;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({ children }) => {
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const confirm = (options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogProps({
        open: true,
        title: options.title,
        message: options.message,
        confirmText: options.confirmText,
        cancelText: options.cancelText,
        confirmButtonColor: options.confirmButtonColor,
        isLoading: false,
        onConfirm: async () => {
          setIsLoading(true);
          // Add a small delay to show loading state
          await new Promise((resolve) => setTimeout(resolve, 100));
          setIsLoading(false);
          setDialogProps(null);
          resolve(true);
        },
        onCancel: () => {
          setDialogProps(null);
          resolve(false);
        },
      });
    });
  };

  const contextValue: ConfirmationContextType = {
    confirm,
  };

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      {dialogProps && <ConfirmationDialog {...dialogProps} isLoading={isLoading} />}
    </ConfirmationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its context provider
export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};
